import { FC, useEffect, useState } from "react";
import useAxios from "../../../../hooks/useAxios";
import { IGetParent, IStudent } from "../../../../models/students.model";
import { getParent } from "../../../../api/students.api";
import { IGetCertificateAmounts } from "../../../../models/payments.model";
import { getCertificateAmounts } from "../../../../api/payments.api";
import Loader from "../../../Loader";
import ErrorResponse from "../../../ErrorResponse";
import { Button as AntButton, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/rootReducer";
import moment from "moment";
import useNotification from "../../../../hooks/useNotification";
import { getReverseYears, getWorkbook } from "../../../../lib/util";
import { getCampus } from "../../../../api/campuses.api";
import { IGetCampus } from "../../../../models/campuses.model";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  student?: IStudent;
}

const DownloadCertificateModal: FC<IProps> = ({
  visible,
  setVisible,
  student,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0005 = 연도
  const SY0005 = commonCodes.find((item) => item.commonCode === 'SY0005')?.detailCode;
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetParent>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetCampus>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetCertificateAmounts>();

  const [learningYearCode, setLearningYearCode] = useState<string>();

  useEffect(() => {
    if (student) {
      execute(getParent(student.studentCode));
      execute2(getCampus(student.campusCode));
    }
  }, [student]);

  useEffect(() => {
    if (serverDate) {
      setLearningYearCode(moment(serverDate).format('YYYY'));
    }
  }, [serverDate]);

  useEffect(() => {
    if (res && !isLoading && res2 && !isLoading2 && res3 && !isLoading3) {
      generateExcelFile({
        studentName: student?.studentKorName,
        address: res.data?.address,
        detailAddress: res.data?.detailAddress,
        campusName: `${t('student.student.75')} ${res2.data?.campusKorName}${t('student.student.76')}`,
        campusAddress: res2.data?.address,
        campusDetailAddress: res2.data?.detailAddress,
        campusTelephoneNo: res2.data?.telephoneNo,
        MonthlyAmounts: res3.data,
        campusRepresentName: res2.data?.representName,
      });
    }
  }, [res, isLoading, res2, isLoading2, res3, isLoading3]);

  const generateExcelFile = (data: any) => {
    getWorkbook('payment_certificate.xlsx')
      .then((workbook: any) => {
        const sheet = workbook.sheet(0);
        sheet.cell('E8').value(data.studentName);
        sheet.cell('E9').value(`${data.address} ${data.detailAddress}`);
        sheet.cell('D12').value(data.campusName);
        sheet.cell('D13').value(`${data.campusAddress} ${data.campusDetailAddress}`);
        sheet.cell('M13').value(data.campusTelephoneNo);

        const paymentCells = ['D19', 'D20', 'D21', 'D22', 'D23', 'D24', 'J19', 'J20', 'J21', 'J22', 'J23', 'J24'];
        data.MonthlyAmounts.forEach((item: any) => {
          const cellIndex = Number(item.monthNameCode) - 1;
          sheet.cell(paymentCells[cellIndex]).value(item.paymentAmount?.toLocaleString());
        });

        const totalAmount = data.MonthlyAmounts.reduce((acc: number, cur: any) => acc + cur.paymentAmount, 0);
        sheet.cell('D25').value(totalAmount?.toLocaleString());

        sheet.cell('G38').value(`${moment(serverDate).format('YYYY')}${t('student.student.77')}`);
        sheet.cell('H38').value(`${moment(serverDate).format('M')}${t('student.student.78')}`);
        sheet.cell('I38').value(`${moment(serverDate).format('D')}${t('student.student.72')}`);
        sheet.cell('I40').value(data.campusRepresentName);

        return workbook.outputAsync();    
      })
      .then(function (blob) {
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.href = url;
        a.download = "payment.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(function (err) {
        alert(err.message || err);
        throw err;
      });
  }

  const onDownload = () => {
    if (!student) return;
    if (!learningYearCode) {
      return infoNotification(t('student.student.message30'));
    }

    execute3(getCertificateAmounts({
      learningYearCode,
      memberCode: student.studentCode
    }));
  }

  return (
    <>
      {(isLoading || isLoading2 || isLoading3) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />

      <Modal
        title={t('student.student.title8')}
        width={400}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onDownload}
            >
              {t('student.student.button15')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setVisible(false)}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex flex-justify-center">
          <div className="v-line mr-2">{t('student.student.column75')}</div>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('student.student.placeholder6')}
              value={learningYearCode}
              onChange={(value) => setLearningYearCode(value)}
            >
              {getReverseYears(SY0005).map(item => (
                <Select.Option
                  key={item.detailCode}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </div>          
        </div>
      </Modal>
    </>
  );
}

export default DownloadCertificateModal;
